import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, MessageCircle, X, ExternalLink } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchPlatformSettings } from '../../store/slices/settingsSlice';

interface SupportTickerProps {
  className?: string;
}

const SupportTicker: React.FC<SupportTickerProps> = ({ className = '' }) => {
  const dispatch = useAppDispatch();
  const { settings } = useAppSelector((state) => state.settings);
  const [isVisible, setIsVisible] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (!settings) {
      dispatch(fetchPlatformSettings());
    }
  }, [dispatch, settings]);

  // Check if ticker was dismissed in this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem('support_ticker_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('support_ticker_dismissed', 'true');
  };

  // Don't render if disabled, no message, or dismissed
  if (!settings?.enable_support_ticker || !settings?.support_ticker_message || isDismissed) {
    return null;
  }

  const tickerMessage = settings.support_ticker_message;
  const tickerLink = settings.support_ticker_link;
  const tickerLinkText = settings.support_ticker_link_text || 'Get Help';
  const tickerSpeed = settings.support_ticker_speed || 'normal';
  const bgColor = settings.support_ticker_bg_color;
  const textColor = settings.support_ticker_text_color;

  // Animation duration based on speed
  const speedMap = {
    slow: 30,
    normal: 20,
    fast: 12,
  };
  const animationDuration = speedMap[tickerSpeed] || 20;

  // Custom styles
  const customStyles: React.CSSProperties = {
    backgroundColor: bgColor || undefined,
    color: textColor || undefined,
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -50, opacity: 0 }}
      className={`relative overflow-hidden ${className}`}
      style={customStyles}
    >
      <div 
        className={`${!bgColor ? 'bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600' : ''} ${!textColor ? 'text-white' : ''}`}
        style={customStyles}
      >
        <div className="container-custom py-2">
          <div className="flex items-center justify-between gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 hidden sm:flex items-center gap-2">
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              >
                <HelpCircle className="w-5 h-5" />
              </motion.div>
              <span className="text-sm font-semibold hidden md:inline">Support</span>
            </div>

            {/* Scrolling Message */}
            <div className="flex-1 overflow-hidden relative">
              <div className="ticker-wrapper">
                <motion.div
                  className="ticker-content flex items-center gap-8 whitespace-nowrap"
                  animate={{ x: ['0%', '-50%'] }}
                  transition={{
                    duration: animationDuration,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                >
                  {/* Duplicate message for seamless loop */}
                  {[1, 2].map((_, idx) => (
                    <div key={idx} className="flex items-center gap-8">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <MessageCircle className="w-4 h-4 flex-shrink-0" />
                        {tickerMessage}
                      </span>
                      {tickerLink && (
                        <a
                          href={tickerLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm font-bold underline underline-offset-2 hover:opacity-80 transition-opacity"
                        >
                          {tickerLinkText}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      <span className="text-white/50">•</span>
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>

            {/* Dismiss Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleDismiss}
              className="flex-shrink-0 p-1.5 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Dismiss ticker"
            >
              <X className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SupportTicker;
